
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Upload, Play, Download, AlertCircle, TrendingUp, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BotMergeAnalysisChart } from './BotMergeAnalysisChart';
import { BotMergeAnalysisStats } from './BotMergeAnalysisStats';
import { MergedTradingHistoryTable } from './MergedTradingHistoryTable';
import { BulkHistoricalUpload } from './BulkHistoricalUpload';
import { useAuth } from '@/contexts/AuthContext';
import { MergeAnalysisService } from '@/services/merge-bots/merge-analysis-service';
import Papa from 'papaparse';

interface ChildBotWithData {
  bot_id: string;
  bot_name: string;
  mapping_id: string;
  csv_data?: Array<{
    timestamp: string;
    win: number;
    pnl: number;
    signal: string;
    price: number;
    position_size: number;
    cumulative_pnl: number;
  }>;
  tp_usd?: number;
  sl_usd?: number;
  hasFile?: boolean;
  instrument?: string;
}

interface TpSlSettings {
  [mappingId: string]: {
    tp_usd: number | null;
    sl_usd: number | null;
    quantity: number | null;
    instrument?: string;
  };
}

interface MergedTradeRow {
  timestamp: string;
  bot_name: string;
  signal: string;
  position_size: number | null;
  original_pnl: number;
  normalized_pnl: number;
  cumulative_pnl: number;
}

interface AnalysisResults {
  equityData: Array<{
    timestamp: string;
    cumulative_pnl: number;
    total_pnl: number;
    bot_contributions: Record<string, number>;
    instrument?: string; // Added instrument to each equity point
  }>;
  detailedTrades: MergedTradeRow[];
  statistics: {
    totalTrades: number;
    winningTrades: number;
    losingTrades: number;
    winRate: number;
    totalPnl: number;
    avgWin: number;
    avgLoss: number;
    profitFactor: number;
    maxDrawdown: number;
    maxDrawdownPercent: number;
    riskRewardRatio: number;
  };
  botContributions: Array<{
    name: string;
    totalPnl: number;
    tradeCount: number;
    winRate: number;
  }>;
}

export const HistoricalAnalysisTab: React.FC = () => {
  const [selectedParentBotId, setSelectedParentBotId] = useState<string>('');
  const [childBotsWithData, setChildBotsWithData] = useState<ChildBotWithData[]>([]);
  const [selectedBots, setSelectedBots] = useState<Set<string>>(new Set());
  const [tpSlSettings, setTpSlSettings] = useState<TpSlSettings>({});
  const [isAnalysisRunning, setIsAnalysisRunning] = useState(false);
  const [analysisResults, setAnalysisResults] = useState<AnalysisResults | null>(null);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [initialCapital, setInitialCapital] = useState(() => {
    const saved = localStorage.getItem('bot-merge-initial-capital');
    return saved ? parseFloat(saved) : 10000;
  });
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Helper function to normalize instrument names
  const normalizeInstrument = (instrument: string): string => {
    return instrument.trim().toUpperCase().replace(/\s+/g, '');
  };

  // Check if user is admin
  const isAdmin = user && ['admin', 'superadmin'].includes(user.user_metadata?.role || '');

  // Publish to Prop Growth mutation
  const publishMutation = useMutation({
    mutationFn: async () => {
      if (!selectedParentBotId || !analysisResults) {
        throw new Error('No parent bot or analysis results available');
      }

      const selectedChildBots = childBotsWithData
        .filter(bot => selectedBots.has(bot.mapping_id));
      
      // Check for missing instruments
      const missingInstruments = selectedChildBots.filter(
        bot => !tpSlSettings[bot.mapping_id]?.instrument?.trim()
      );
      
      if (missingInstruments.length > 0) {
        throw new Error(
          `Please specify instruments for: ${missingInstruments.map(b => b.bot_name).join(', ')}`
        );
      }

      // Convert analysis results to the format expected by the service
      const equityCurve = analysisResults.equityData.map((point: any, index: number) => ({
        trade_index: index + 1,
        pnl: point.total_pnl || 0,
        equity: initialCapital + point.cumulative_pnl,
        t: point.timestamp || null,
        instrument: point.instrument // Include per-point instrument
      }));
      
      const childBotIds = selectedChildBots.map(bot => bot.bot_id);
      
      const config = Object.fromEntries(
        selectedChildBots.map(bot => [bot.bot_id, tpSlSettings[bot.mapping_id]])
      );

      // Determine instrument for the merged analysis with normalization
      const instruments = selectedChildBots
        .map(bot => tpSlSettings[bot.mapping_id]?.instrument)
        .filter(Boolean)
        .map(normalizeInstrument) as string[];
      
      const uniqueInstruments = [...new Set(instruments)];
      const instrument = uniqueInstruments.length === 1 ? uniqueInstruments[0] : 
                        uniqueInstruments.length > 1 ? 'MULTI' : undefined;

      // Show warning for multi-instrument analysis
      if (uniqueInstruments.length > 1) {
        console.warn('Publishing multi-instrument analysis:', uniqueInstruments);
      }

      // Normalize detailed trades data for Prop Trading table
      const detailedTrades = analysisResults.detailedTrades.map((trade: any) => {
        // Find the instrument for this bot
        const botConfig = selectedChildBots.find(bot => bot.bot_name === trade.bot_name);
        const instrument = botConfig ? 
          normalizeInstrument(tpSlSettings[botConfig.mapping_id]?.instrument || 'UNKNOWN') : 
          'UNKNOWN';
        
        // Normalize action
        const signal = trade.signal?.toLowerCase() || '';
        let action = 'UNKNOWN';
        if (signal.includes('long') || signal.includes('buy')) {
          action = 'LONG';
        } else if (signal.includes('short') || signal.includes('sell')) {
          action = 'SHORT';
        }
        
        return {
          timestamp: trade.timestamp,
          instrument: instrument,
          amount: trade.position_size || 0,
          action: action,
          normalized_pnl: trade.normalized_pnl || 0 // Fixed: use correct field
        };
      });

      await MergeAnalysisService.publishMergeAnalysis({
        parent_bot_id: selectedParentBotId,
        child_bot_ids: childBotIds,
        config,
        equity_curve: equityCurve,
        stats: {
          ...analysisResults.statistics,
          detailed_trades: detailedTrades // Add detailed trades for Prop Trading page
        },
        published: true,
        is_current: true,
        title: `Merged Analysis - ${new Date().toLocaleDateString()}`,
        notes: `Analysis of ${childBotIds.length} child bots with TP/SL normalization`,
        instrument
      });
    },
    onSuccess: () => {
      toast.success('Analysis published to Prop Growth successfully!', {
        description: 'This analysis will now be displayed in the Prop Trading Bot growth chart.'
      });
      // Invalidate prop bot growth queries to refresh the chart
      queryClient.invalidateQueries({ queryKey: ['propBotGrowth'] });
    },
    onError: (error) => {
      console.error('Error publishing analysis:', error);
      toast.error('Failed to publish analysis');
    }
  });

  // Fetch parent bots (all bots for admin, user's bots for others)
  const { data: parentBots } = useQuery({
    queryKey: ['parent-bots'],
    queryFn: async () => {
      let query = supabase
        .from('bots')
        .select('id, name');
      
      // Only filter by owner_id if user is not admin
      if (!isAdmin) {
        query = query.eq('owner_id', (await supabase.auth.getUser()).data.user?.id);
      }
      
      const { data, error } = await query.order('name');
      
      if (error) throw error;
      return data;
    },
  });

  // Fetch child bots and their files when parent bot selected
  const { data: mappings } = useQuery({
    queryKey: ['child-bots-with-data', selectedParentBotId],
    queryFn: async () => {
      if (!selectedParentBotId) return [];

      const { data, error } = await supabase
        .from('bot_merge_mapping')
        .select(`
          id,
          child_bot_id,
          child_bot:bots!child_bot_id(name),
          bot_merge_history_files(id, file_data, filename, instrument)
        `)
        .eq('parent_bot_id', selectedParentBotId)
        .eq('is_active', true);

      if (error) throw error;
      
      return data.map(mapping => ({
        bot_id: mapping.child_bot_id,
        mapping_id: mapping.id,
        bot_name: mapping.child_bot.name,
        hasFile: mapping.bot_merge_history_files.length > 0,
        instrument: mapping.bot_merge_history_files[0]?.instrument || undefined,
        csv_data: mapping.bot_merge_history_files.length > 0 
          ? parseCSVData(mapping.bot_merge_history_files[0].file_data)
          : undefined
      }));
    },
    enabled: !!selectedParentBotId,
  });

  // Helper function to resolve number from multiple possible column names
  const resolveNumber = (row: any, keys: string[]): number => {
    for (const key of keys) {
      const value = row[key];
      if (value !== undefined && value !== null && value !== '') {
        const parsed = parseFloat(value.toString().replace(/[^-0-9.]/g, ''));
        if (!isNaN(parsed)) {
          return parsed;
        }
      }
    }
    return 0;
  };

  // Parse CSV data with proper handling for multiple column name variations
  const parseCSVData = (fileData: any) => {
    if (!fileData || !Array.isArray(fileData)) return [];

    let unparsedPnlCount = 0;
    const totalRows = fileData.length;

    try {
      const results = fileData.map((row: any, index: number) => {
        const signal = row['Signal'] || row['signal'] || '';
        
        // Try multiple P&L column names
        const pnlKeys = ['Net P&L USD', 'Net P&L USDT', 'P&L USD', 'P&L USDT', 'Net P&L', 'P&L', 'pnl'];
        const pnl = resolveNumber(row, pnlKeys);
        
        // If no absolute P&L found, try percentage columns for sign only
        if (pnl === 0) {
          const pnlPercentKeys = ['Net P&L %', 'P&L %'];
          const pnlPercent = resolveNumber(row, pnlPercentKeys);
          if (pnlPercent === 0) {
            unparsedPnlCount++;
          }
        }
        
        const dateStr = row['Date/Time'] || row['datetime'] || row['timestamp'] || '';
        
        // Try multiple price column names  
        const priceKeys = ['Price USD', 'Price USDT', 'Price', 'price'];
        const price = resolveNumber(row, priceKeys);
        
        // Try multiple position size column names
        const positionKeys = ['Position size (qty)', 'Position size qty', 'Position size', 'Qty', 'position_size'];
        const positionSize = resolveNumber(row, positionKeys);
        
        // Try multiple cumulative P&L column names
        const cumulativeKeys = ['Cumulative P&L USD', 'Cumulative P&L USDT', 'Cumulative P&L', 'cumulative_pnl'];
        const cumulativePnl = resolveNumber(row, cumulativeKeys);

        return {
          timestamp: dateStr,
          win: pnl > 0 ? 1 : 0, // Only for legacy compatibility - we'll use pnl directly in analysis
          pnl,
          signal,
          price,
          position_size: positionSize,
          cumulative_pnl: cumulativePnl
        };
      });

      // Log parsing results for debugging
      console.log('CSV Parsing Results:', {
        totalRows,
        unparsedPnlCount,
        unparsedPercentage: totalRows > 0 ? (unparsedPnlCount / totalRows * 100).toFixed(1) + '%' : '0%',
        sampleRow: results[0]
      });

      return results;
    } catch (error) {
      console.error('Error parsing CSV data:', error);
      return [];
    }
  };

  // Load existing TP/SL/Quantity settings
  React.useEffect(() => {
    if (!mappings || mappings.length === 0) return;

    const loadSettings = async () => {
      const { data, error } = await supabase
        .from('bot_merge_analysis_settings')
        .select('child_bot_id, instrument, tp_usd, sl_usd, quantity')
        .eq('parent_bot_id', selectedParentBotId);

      if (error) {
        console.error('Error loading settings:', error);
        return;
      }

      const settingsMap: TpSlSettings = {};
      mappings.forEach(mapping => {
        const setting = data?.find(s => s.child_bot_id === mapping.bot_id);
        settingsMap[mapping.mapping_id] = {
          tp_usd: setting?.tp_usd ?? null,
          sl_usd: setting?.sl_usd ?? null,
          quantity: setting?.quantity ?? null,
          instrument: setting?.instrument || mapping.instrument || '',
        };
      });
      setTpSlSettings(settingsMap);
    };

    loadSettings();
  }, [mappings, selectedParentBotId]);

  React.useEffect(() => {
    if (mappings) {
      setChildBotsWithData(mappings);
      // Auto-select all bots with data when mappings change
      const botsWithData = mappings.filter(bot => bot.hasFile);
      setSelectedBots(new Set(botsWithData.map(bot => bot.mapping_id)));
    }
  }, [mappings]);

  // Helper function to extract instrument from filename
  const extractInstrumentFromFilename = (filename: string): string => {
    // Pattern: "EURUSD - 0.2" or "ETHUSDT - 1.8 - 5s" 
    const match = filename.match(/^([A-Z]{6,8})/);
    return match ? match[1] : '';
  };

  // Helper function to detect CSV Analysis metadata
  const detectCsvAnalysisConfig = (headers: string[], row: any) => {
    const hasConfigFlag = headers.includes('__CSV_ANALYSIS_CONFIG__');
    if (!hasConfigFlag) return null;

    // Helper to safely parse float, preserving 0 values
    const safeParseFloat = (value: any) => {
      if (value === null || value === undefined || value === '') return null;
      const parsed = parseFloat(value);
      return isNaN(parsed) ? null : parsed;
    };

    const config = {
      tp_usd: safeParseFloat(row['__TP_USD__']),
      sl_usd: safeParseFloat(row['__SL_USD__']),
      quantity: safeParseFloat(row['__QUANTITY_BASE__']),
      instrument: row['__INSTRUMENT__'] || ''
    };

    console.log('Detected CSV Analysis config:', config);
    return config;
  };

  // File upload handler
  const handleFileUpload = async (mappingId: string, botId: string, file: File) => {
    try {
      const text = await file.text();
      
      // Use PapaParse for better CSV parsing
      const parsed = Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => header.trim(),
      });

      if (parsed.errors.length > 0) {
        console.warn('CSV parsing errors:', parsed.errors);
      }

      let unparsedPnlCount = 0;
      const totalRows = parsed.data.length;

      // Check for CSV Analysis metadata in first row
      const headers = Object.keys(parsed.data[0] || {});
      const csvAnalysisConfig = parsed.data.length > 0 ? detectCsvAnalysisConfig(headers, parsed.data[0]) : null;

      // Filter out metadata columns during processing
      const filteredData = parsed.data.map((row: any) => {
        const filteredRow = { ...row };
        // Remove metadata columns that shouldn't affect trading data
        delete filteredRow['__TP_USD__'];
        delete filteredRow['__SL_USD__'];
        delete filteredRow['__QUANTITY_BASE__'];
        delete filteredRow['__INSTRUMENT__'];
        delete filteredRow['__CSV_ANALYSIS_CONFIG__'];
        return filteredRow;
      });

      const normalizedData = filteredData.map((row: any, index: number) => {
        const signal = row['Signal'] || row['signal'] || '';
        
        // Try multiple P&L column names
        const pnlKeys = ['Net P&L USD', 'Net P&L USDT', 'P&L USD', 'P&L USDT', 'Net P&L', 'P&L', 'pnl'];
        const pnl = resolveNumber(row, pnlKeys);
        
        // If no absolute P&L found, count as unparsed
        if (pnl === 0) {
          const pnlPercentKeys = ['Net P&L %', 'P&L %'];
          const pnlPercent = resolveNumber(row, pnlPercentKeys);
          if (pnlPercent === 0) {
            unparsedPnlCount++;
          }
        }
        
        const dateStr = row['Date/Time'] || row['datetime'] || row['timestamp'] || '';
        
        // Try multiple price column names  
        const priceKeys = ['Price USD', 'Price USDT', 'Price', 'price'];
        const price = resolveNumber(row, priceKeys);
        
        // Try multiple position size column names
        const positionKeys = ['Position size (qty)', 'Position size qty', 'Position size', 'Qty', 'position_size'];
        const positionSize = resolveNumber(row, positionKeys);
        
        // Try multiple cumulative P&L column names
        const cumulativeKeys = ['Cumulative P&L USD', 'Cumulative P&L USDT', 'Cumulative P&L', 'cumulative_pnl'];
        const cumulativePnl = resolveNumber(row, cumulativeKeys);

        return {
          timestamp: dateStr,
          win: pnl > 0 ? 1 : 0, // Only for legacy compatibility - we'll use pnl directly in analysis
          pnl,
          signal,
          price,
          position_size: positionSize,
          cumulative_pnl: cumulativePnl
        };
      });

      // Log parsing results for debugging
      console.log('CSV Upload Parsing Results:', {
        filename: file.name,
        totalRows,
        unparsedPnlCount,
        unparsedPercentage: totalRows > 0 ? (unparsedPnlCount / totalRows * 100).toFixed(1) + '%' : '0%',
        sampleRow: normalizedData[0],
        availableColumns: Object.keys(parsed.data[0] || {})
      });

      // Auto-populate settings from CSV Analysis metadata or filename
      let autoInstrument = '';
      let autoTpUsd = null;
      let autoSlUsd = null;
      let autoQuantity = null;

      if (csvAnalysisConfig) {
        // Use metadata from CSV Analysis
        autoTpUsd = csvAnalysisConfig.tp_usd;
        autoSlUsd = csvAnalysisConfig.sl_usd;
        autoQuantity = csvAnalysisConfig.quantity;
        autoInstrument = csvAnalysisConfig.instrument;
        
        toast.success(`Configuration auto-loaded from CSV Analysis: TP=$${autoTpUsd !== null ? autoTpUsd : 'N/A'}, SL=$${autoSlUsd !== null ? autoSlUsd : 'N/A'}, Qty=${autoQuantity !== null ? autoQuantity : 'N/A'}, Instrument=${autoInstrument || 'N/A'}`, {
          duration: 4000
        });
      } else {
        // Extract instrument from filename pattern
        autoInstrument = extractInstrumentFromFilename(file.name);
        if (autoInstrument) {
          toast.info(`Instrument detected from filename: ${autoInstrument}`);
        }
      }

      // Get current instrument setting for this mapping (prefer auto-detected)
      const currentInstrument = autoInstrument || tpSlSettings[mappingId]?.instrument || '';

      // Auto-populate TP/SL/Quantity settings if detected from CSV Analysis
      if (csvAnalysisConfig) {
        console.log('Auto-populating settings with CSV Analysis config:', {
          tp_usd: autoTpUsd,
          sl_usd: autoSlUsd, 
          quantity: autoQuantity,
          instrument: autoInstrument
        });
        
        setTpSlSettings(prev => ({
          ...prev,
          [mappingId]: {
            tp_usd: autoTpUsd !== null ? autoTpUsd : prev[mappingId]?.tp_usd || null,
            sl_usd: autoSlUsd !== null ? autoSlUsd : prev[mappingId]?.sl_usd || null,
            quantity: autoQuantity !== null ? autoQuantity : prev[mappingId]?.quantity || null,
            instrument: normalizeInstrument(autoInstrument) || prev[mappingId]?.instrument || '',
          },
        }));
      } else if (autoInstrument) {
        // Only update instrument if detected from filename
        setTpSlSettings(prev => ({
          ...prev,
          [mappingId]: {
            ...prev[mappingId],
            instrument: normalizeInstrument(autoInstrument),
          },
        }));
      }

      // Save or update file with mapping_id and instrument
      const { error } = await supabase
        .from('bot_merge_history_files')
        .upsert({
          mapping_id: mappingId,
          bot_id: botId,
          filename: file.name,
          file_data: normalizedData,
          file_size: file.size,
          instrument: normalizeInstrument(currentInstrument) || null,
        }, {
          onConflict: 'mapping_id,bot_id'
        });

      if (error) throw error;

      const botName = childBotsWithData.find(b => b.mapping_id === mappingId)?.bot_name;
      toast.success(`File uploaded for ${botName}${csvAnalysisConfig ? ' with auto-configuration' : ''}`);
      queryClient.invalidateQueries({ queryKey: ['child-bots-with-data'] });
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    }
  };

  // Update TP/SL/Quantity/Instrument
  const updateTpSl = (mappingId: string, field: 'tp_usd' | 'sl_usd' | 'quantity' | 'instrument', value: string) => {
    let processedValue: any;
    if (field === 'instrument') {
      processedValue = normalizeInstrument(value);
    } else {
      if (value.trim() === '') {
        processedValue = null;
      } else {
        const parsed = parseFloat(value);
        processedValue = isNaN(parsed) ? null : parsed;
      }
    }
    console.log('updateTpSl:', { mappingId, field, raw: value, processed: processedValue });
    setTpSlSettings(prev => ({
      ...prev,
      [mappingId]: {
        ...prev[mappingId],
        [field]: processedValue,
      },
    }));
  };

  // Update instrument in database when it changes
  const updateInstrument = async (mappingId: string, botId: string, instrument: string) => {
    try {
      const normalizedInstrument = normalizeInstrument(instrument);
      const { error } = await supabase
        .from('bot_merge_history_files')
        .update({ instrument: normalizedInstrument || null })
        .eq('mapping_id', mappingId)
        .eq('bot_id', botId);

      if (error) throw error;
      
      toast.success('Instrument updated successfully');
    } catch (error) {
      console.error('Error updating instrument:', error);
      toast.error('Failed to update instrument');
    }
  };

  // Save configuration
  const saveConfigMutation = useMutation({
    mutationFn: async () => {
      if (!selectedParentBotId) throw new Error('No parent bot selected');

      const promises = childBotsWithData.map(async (childBot) => {
        const settings = tpSlSettings[childBot.mapping_id];
        if (!settings || (settings.tp_usd === null && settings.sl_usd === null)) {
          return;
        }

        // Save to bot_merge_analysis_settings (historical analysis)
        const { error: analysisError } = await supabase
          .from('bot_merge_analysis_settings')
          .upsert({
            parent_bot_id: selectedParentBotId,
            child_bot_id: childBot.bot_id,
            instrument: settings.instrument || null,
            tp_usd: settings.tp_usd,
            sl_usd: settings.sl_usd,
            quantity: settings.quantity,
          }, {
            onConflict: 'parent_bot_id,child_bot_id'
          });

        if (analysisError) throw analysisError;

        // Auto-sync to bot_merge_mapping (realtime P&L calculation)
        const { error: mappingError } = await supabase
          .from('bot_merge_mapping')
          .update({
            instrument: settings.instrument || null,
            tp_usd: settings.tp_usd,
            sl_usd: settings.sl_usd,
            quantity: settings.quantity,
          })
          .eq('id', childBot.mapping_id);

        if (mappingError) {
          console.warn('Failed to sync to bot_merge_mapping:', mappingError);
          // Don't throw - analysis settings are primary, mapping sync is secondary
        }
      });

      await Promise.all(promises);
    },
    onSuccess: () => {
      toast.success('Configuration saved & synced successfully', {
        description: 'Config synced for both Historical Analysis and Realtime P&L calculation'
      });
      queryClient.invalidateQueries({ queryKey: ['bot-mappings'] });
    },
    onError: (error) => {
      console.error('Error saving configuration:', error);
      toast.error('Failed to save configuration');
    },
  });

  // Run comprehensive analysis
  const runAnalysis = async () => {
    if (!selectedParentBotId || childBotsWithData.length === 0) {
      toast.error('Please select a parent bot and upload trading history files');
      return;
    }

    const selectedBotsWithData = childBotsWithData.filter(bot => 
      selectedBots.has(bot.mapping_id) && bot.csv_data && bot.csv_data.length > 0
    );
    
    if (selectedBots.size === 0) {
      toast.error('Please select at least one bot for analysis.');
      return;
    }
    
    if (selectedBotsWithData.length === 0) {
      toast.error('Selected bots have no trading history data. Please upload CSV files first.');
      return;
    }

    setIsAnalysisRunning(true);

    try {
      // Collect all trades from all bots and sort by timestamp
      const allTrades: Array<{
        timestamp: string;
        pnl: number;
        normalized_pnl: number;
        bot_name: string;
        original_pnl: number;
        instrument: string;
      }> = [];

      selectedBotsWithData.forEach(bot => {
        const settings = tpSlSettings[bot.mapping_id];
        const tp = settings?.tp_usd || 0;
        const sl = settings?.sl_usd || 0;
        const quantity = settings?.quantity || 0;
        const instrument = normalizeInstrument(settings?.instrument || '');

        bot.csv_data?.forEach(trade => {
          // Calculate position quantity ratio (pq)
          const pq = quantity > 0 ? (trade.position_size || 0) / quantity : 1;
          
          // Determine outcome based on P&L sign directly (not trade.win flag)
          const outcomeSign = trade.pnl > 0 ? 1 : (trade.pnl < 0 ? -1 : 0);
          
          // Apply pq scaling to TP/SL based on actual P&L sign
          let normalizedPnl;
          if (outcomeSign > 0) {
            // Winning trade
            normalizedPnl = tp > 0 ? tp * pq : trade.pnl;
          } else if (outcomeSign < 0) {
            // Losing trade
            normalizedPnl = sl > 0 ? -sl * pq : trade.pnl;
          } else {
            // Zero P&L trade
            normalizedPnl = 0;
          }
          
          allTrades.push({
            timestamp: trade.timestamp,
            pnl: trade.pnl,
            normalized_pnl: normalizedPnl,
            bot_name: bot.bot_name,
            original_pnl: trade.pnl,
            instrument: instrument
          });
        });
      });

      // Sort by timestamp
      allTrades.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

      // Calculate equity curve and statistics
      let cumulativePnl = 0;
      let peak = initialCapital;
      let maxDrawdown = 0;
      let maxDrawdownPercent = 0;

      const equityData: Array<{
        timestamp: string;
        cumulative_pnl: number;
        total_pnl: number;
        bot_contributions: Record<string, number>;
        instrument?: string;
      }> = [];

      const detailedTrades: MergedTradeRow[] = [];

      allTrades.forEach(trade => {
        cumulativePnl += trade.normalized_pnl;
        const equity = initialCapital + cumulativePnl;
        
        // Update peak and drawdown using equity (not just P&L)
        if (equity > peak) {
          peak = equity;
        }
        
        const drawdown = peak - equity;
        if (drawdown > maxDrawdown) {
          maxDrawdown = drawdown;
          maxDrawdownPercent = initialCapital > 0 ? (drawdown / initialCapital) * 100 : 0;
        }

        const botContributions: Record<string, number> = {};
        selectedBotsWithData.forEach(bot => {
          const botTrade = allTrades.find(t => 
            t.timestamp === trade.timestamp && t.bot_name === bot.bot_name
          );
          botContributions[bot.bot_name] = botTrade?.normalized_pnl || 0;
        });

        // Add to equity data
        equityData.push({
          timestamp: trade.timestamp,
          cumulative_pnl: cumulativePnl,
          total_pnl: trade.normalized_pnl,
          bot_contributions: botContributions,
          instrument: trade.instrument
        });

        // Add to detailed trades for the table
        const originalTradeData = selectedBotsWithData.find(bot => bot.bot_name === trade.bot_name)
          ?.csv_data?.find(csvTrade => csvTrade.timestamp === trade.timestamp);

        detailedTrades.push({
          timestamp: trade.timestamp,
          bot_name: trade.bot_name,
          signal: originalTradeData?.signal || 'Unknown',
          position_size: originalTradeData?.position_size || null,
          original_pnl: trade.original_pnl,
          normalized_pnl: trade.normalized_pnl,
          cumulative_pnl: cumulativePnl
        });
      });

      // Calculate comprehensive statistics
      const totalTrades = allTrades.length;
      const winningTrades = allTrades.filter(t => t.normalized_pnl > 0).length;
      const losingTrades = allTrades.filter(t => t.normalized_pnl < 0).length;
      const winRate = totalTrades > 0 ? (winningTrades / totalTrades) * 100 : 0;

      const wins = allTrades.filter(t => t.normalized_pnl > 0);
      const losses = allTrades.filter(t => t.normalized_pnl < 0);
      
      const avgWin = wins.length > 0 ? wins.reduce((sum, t) => sum + t.normalized_pnl, 0) / wins.length : 0;
      const avgLoss = losses.length > 0 ? Math.abs(losses.reduce((sum, t) => sum + t.normalized_pnl, 0) / losses.length) : 0;
      
      const profitFactor = avgLoss > 0 ? avgWin / avgLoss : 0;
      const riskRewardRatio = avgLoss > 0 ? avgWin / avgLoss : 0;

      // Calculate bot contributions
      const botContributions = selectedBotsWithData.map(bot => {
        const botTrades = allTrades.filter(t => t.bot_name === bot.bot_name);
        const botWins = botTrades.filter(t => t.normalized_pnl > 0).length;
        const botWinRate = botTrades.length > 0 ? (botWins / botTrades.length) * 100 : 0;
        
        return {
          name: bot.bot_name,
          totalPnl: botTrades.reduce((sum, t) => sum + t.normalized_pnl, 0),
          tradeCount: botTrades.length,
          winRate: botWinRate
        };
      });

      setAnalysisResults({
        equityData,
        detailedTrades,
        statistics: {
          totalTrades,
          winningTrades,
          losingTrades,
          winRate,
          totalPnl: cumulativePnl,
          avgWin,
          avgLoss,
          profitFactor,
          maxDrawdown,
          maxDrawdownPercent,
          riskRewardRatio
        },
        botContributions
      });

      toast.success('Comprehensive analysis completed successfully');
    } catch (error) {
      console.error('Error running analysis:', error);
      toast.error('Failed to run analysis');
    } finally {
      setIsAnalysisRunning(false);
    }
  };

  if (!selectedParentBotId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Historical Analysis & Trading History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <Label htmlFor="parent-bot">Select Parent Bot</Label>
              <select
                id="parent-bot"
                className="w-full p-2 border rounded-md"
                value={selectedParentBotId}
                onChange={(e) => setSelectedParentBotId(e.target.value)}
              >
                <option value="">Choose a parent bot...</option>
                {parentBots?.map(bot => (
                  <option key={bot.id} value={bot.id}>{bot.name}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Upload Trading History & Configure TP/SL</CardTitle>
              <p className="text-sm text-muted-foreground">
                Upload CSV files for each child bot and configure TP/SL values for normalized analysis
              </p>
            </div>
            <Button 
              onClick={() => setShowBulkUpload(true)}
              className="flex items-center gap-2"
            >
              <Upload className="h-4 w-4" />
              Bulk Upload
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {childBotsWithData.length > 0 && (
            <div className="flex gap-2 mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const botsWithData = childBotsWithData.filter(bot => bot.hasFile);
                  setSelectedBots(new Set(botsWithData.map(bot => bot.mapping_id)));
                }}
              >
                Select All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedBots(new Set())}
              >
                Unselect All
              </Button>
              <span className="text-sm text-muted-foreground self-center">
                {selectedBots.size} of {childBotsWithData.filter(bot => bot.hasFile).length} bots selected for analysis
              </span>
            </div>
          )}
          <div className="grid gap-4">
            {childBotsWithData.map((bot) => {
              const settings = tpSlSettings[bot.mapping_id] || { tp_usd: null, sl_usd: null, quantity: null };
              
              return (
                <div key={bot.mapping_id} className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={selectedBots.has(bot.mapping_id)}
                        onCheckedChange={(checked) => {
                          const newSelected = new Set(selectedBots);
                          if (checked) {
                            newSelected.add(bot.mapping_id);
                          } else {
                            newSelected.delete(bot.mapping_id);
                          }
                          setSelectedBots(newSelected);
                        }}
                        disabled={!bot.hasFile}
                      />
                      <h4 className="font-medium">{bot.bot_name}</h4>
                      {!bot.hasFile && (
                        <span className="text-xs text-muted-foreground">(no data uploaded)</span>
                      )}
                    </div>
                   <div className="flex items-center gap-2">
                       {bot.hasFile && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                            File uploaded ({bot.csv_data?.length || 0} trades)
                            {bot.instrument && ` • ${bot.instrument}`}
                          </span>
                        )}
                       {bot.hasFile && bot.csv_data && (() => {
                         const unparsedCount = bot.csv_data.filter(trade => trade.pnl === 0).length;
                         const unparsedPercentage = bot.csv_data.length > 0 ? (unparsedCount / bot.csv_data.length * 100) : 0;
                         return unparsedPercentage > 20 ? (
                           <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded flex items-center gap-1">
                             <AlertCircle size={12} />
                             P&L parsing issues
                           </span>
                         ) : null;
                       })()}
                      <input
                        type="file"
                        accept=".csv"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleFileUpload(bot.mapping_id, bot.bot_id, file);
                          }
                        }}
                        className="hidden"
                        id={`file-${bot.mapping_id}`}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById(`file-${bot.mapping_id}`)?.click()}
                      >
                        <Upload size={16} className="mr-1" />
                        {bot.hasFile ? 'Replace File' : 'Upload CSV'}
                      </Button>
                    </div>
                  </div>
                  
                   <div className="grid grid-cols-4 gap-4">
                     <div>
                       <Label htmlFor={`instrument-${bot.mapping_id}`}>Instrument</Label>
                       <Input
                         id={`instrument-${bot.mapping_id}`}
                         type="text"
                         placeholder="e.g., XAUUSD"
                         value={settings.instrument || ''}
                         onChange={(e) => {
                           updateTpSl(bot.mapping_id, 'instrument', e.target.value);
                         }}
                         onBlur={(e) => {
                           // Update instrument in database when input loses focus
                           updateInstrument(bot.mapping_id, bot.bot_id, e.target.value);
                         }}
                       />
                       {!settings.instrument && (
                         <p className="text-xs text-amber-600 mt-1">
                           Please specify trading instrument
                         </p>
                       )}
                     </div>
                     <div>
                       <Label htmlFor={`tp-${bot.mapping_id}`}>TP per trade ($)</Label>
                       <Input
                         id={`tp-${bot.mapping_id}`}
                         type="number"
                         min="0"
                         step="0.01"
                         placeholder="e.g., 100"
                         value={settings.tp_usd !== null && settings.tp_usd !== undefined ? settings.tp_usd : ''}
                         onChange={(e) => updateTpSl(bot.mapping_id, 'tp_usd', e.target.value)}
                       />
                     </div>
                     <div>
                       <Label htmlFor={`sl-${bot.mapping_id}`}>SL per trade ($)</Label>
                       <Input
                         id={`sl-${bot.mapping_id}`}
                         type="number"
                         min="0"
                         step="0.01"
                         placeholder="e.g., 50"
                         value={settings.sl_usd !== null && settings.sl_usd !== undefined ? settings.sl_usd : ''}
                         onChange={(e) => updateTpSl(bot.mapping_id, 'sl_usd', e.target.value)}
                       />
                     </div>
                     <div>
                       <Label htmlFor={`quantity-${bot.mapping_id}`}>Quantity (base)</Label>
                       <Input
                         id={`quantity-${bot.mapping_id}`}
                         type="number"
                         min="0"
                         step="0.0001"
                         placeholder="e.g., 1000"
                         value={settings.quantity !== null && settings.quantity !== undefined ? settings.quantity : ''}
                         onChange={(e) => updateTpSl(bot.mapping_id, 'quantity', e.target.value)}
                       />
                       <p className="text-xs text-muted-foreground mt-1">
                         Used to normalize variable position sizes
                       </p>
                     </div>
                   </div>
                </div>
              );
            })}
          </div>

          <div className="space-y-4">
            <div className="p-4 border rounded-lg bg-muted/20">
              <Label htmlFor="initial-capital">Initial Capital ($)</Label>
              <Input
                id="initial-capital"
                type="number"
                min="1"
                step="100"
                placeholder="e.g., 10000"
                value={initialCapital}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 10000;
                  setInitialCapital(value);
                  localStorage.setItem('bot-merge-initial-capital', value.toString());
                }}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Base capital for calculating MaxDD% and equity values (default: $10,000)
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => saveConfigMutation.mutate()}
                disabled={saveConfigMutation.isPending}
                variant="outline"
              >
                <Download size={16} className="mr-2" />
                Save Configuration
              </Button>
              
              <Button
                onClick={runAnalysis}
                disabled={isAnalysisRunning || selectedBots.size === 0}
              >
                <Play size={16} className="mr-2" />
                {isAnalysisRunning ? 'Running Analysis...' : 'Run Comprehensive Analysis'}
              </Button>
            </div>
          </div>

          {analysisResults && (
            <div className="space-y-6 border-t pt-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                  Analysis Results
                </h3>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => publishMutation.mutate()}
                    disabled={publishMutation.isPending}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                  >
                    {publishMutation.isPending ? (
                      <>
                        <Upload className="mr-2 h-4 w-4 animate-spin" />
                        Publishing...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Publish to Prop Growth
                      </>
                    )}
                  </Button>
                  <span className="text-sm text-muted-foreground">TP/SL Normalized</span>
                </div>
              </div>
              
              <BotMergeAnalysisStats statistics={analysisResults.statistics} />
              
              <BotMergeAnalysisChart
                data={analysisResults.equityData}
                title="Merged Equity Curve (TP/SL Normalized)"
                interactiveMode={true}
                initialCapital={initialCapital}
              />

              {/* Merged Trading History Table */}
              <MergedTradingHistoryTable
                data={analysisResults.detailedTrades}
                title="Merged Trading History (TP/SL Normalized)"
              />

              {/* Bot Contributions */}
              <Card>
                <CardHeader>
                  <CardTitle>Individual Bot Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4">
                    {analysisResults.botContributions.map((bot, index) => (
                      <div key={index} className="flex justify-between items-center p-3 bg-muted/20 rounded">
                        <span className="font-medium">{bot.name}</span>
                        <div className="flex gap-4 text-sm">
                          <span className={bot.totalPnl >= 0 ? 'text-green-600' : 'text-red-600'}>
                            ${bot.totalPnl.toFixed(2)}
                          </span>
                          <span className="text-muted-foreground">{bot.tradeCount} trades</span>
                          <span className="text-muted-foreground">{bot.winRate.toFixed(1)}% win</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk Upload Dialog */}
      <BulkHistoricalUpload
        isOpen={showBulkUpload}
        onClose={() => setShowBulkUpload(false)}
        parentBotId={selectedParentBotId}
        availableBots={childBotsWithData.map(bot => ({
          id: bot.bot_id,
          name: bot.bot_name,
          mapping_id: bot.mapping_id
        }))}
        onUploadComplete={() => {
          queryClient.invalidateQueries({ queryKey: ['child-bots-with-data'] });
        }}
      />
    </div>
  );
};
